import React, { useEffect, useState } from 'react';
import { FieldArray, Formik } from 'formik';
import { Button, Container } from 'react-bootstrap';
import { DatePicker } from 'antd';
import Plus from 'mdi-react/PlusIcon';
import Minus from 'mdi-react/CloseIcon';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, Input, Select } from '../../../shared/Forms';
import styles from './Resources.module.scss';
import TopBorderBtn from '../../../shared/TopBorderBtn';
import validateResourcesSchema from './schema';
import TitleUndescore from '../../../shared/TitleUndescore';
import ParalelArivals from './components';
import { branchTitleSelector } from '../../../common/LogoutBtn/selectors';
import { getLocationsRequest, getResourcesRequest, putResourcesRequest } from './services/actions';
import Loader from '../../../kiosk/components/Loader';
import { directorResourcesSelector } from './services/selector';

const Resources: React.FC = () => {
  const [state, setState] = useState(false);
  const branch = useSelector(branchTitleSelector);

  const { locations, resource, loading } = useSelector(directorResourcesSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getResourcesRequest(branch.id));
  }, []);

  useEffect(() => {
    dispatch(getLocationsRequest(branch.id));
  }, []);

  return (
    <>
      {loading && <Loader />}
      <Container>
        <Formik
          initialValues={resource}
          validationSchema={validateResourcesSchema}
          validateOnMount
          enableReinitialize // @ts-ignore
          onSubmit={(values) => dispatch(putResourcesRequest(values))}
        >
          {({ handleSubmit, handleChange, setFieldValue, handleBlur, values, errors }) => (
            <form onSubmit={handleSubmit}>
              <FieldArray
                name="cranes"
                render={(arrayHelpers) => {
                  const addHandler = () => {
                    arrayHelpers.push({
                      locationId: '',
                      name: '',
                      isAvailable: false,
                      accessibilityRowMin: null,
                      accessibilityRowMax: null,
                      stopFrom: moment(),
                      startFrom: moment(),
                      isRuleEnabled: false,
                      branchId: branch.id,
                    });

                    setState(!state);
                  };

                  const deleteHandler = (idx: number) => {
                    arrayHelpers.remove(idx);
                  };

                  return (
                    <section className={styles.wrapper}>
                      <div className={styles.saveSection}>
                        <TopBorderBtn
                          icon="save"
                          disabled={Object.keys(errors).length !== 0}
                          clickHandler={() => dispatch(putResourcesRequest(values))}
                        />
                      </div>
                      <div className={styles.header}>
                        <h4 className="mb-1">Крани</h4>
                        <Button
                          variant="success"
                          className="d-flex justify-content-center align-items-center mr-2 p-1 ml-5 rounded-circle"
                          onClick={addHandler}
                        >
                          <Plus size={13} />
                        </Button>
                        <span>Додати</span>
                      </div>
                      {values.cranes?.map((val: any, index: number) => (
                        <div className={styles.bodyRow}>
                          <span>{index + 1}.</span>
                          <div className={styles.storage}>
                            <span>Склад:</span>
                            <Select
                              name={`cranes[${index}].locationId`}
                              className={styles.storageSelect}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              onLine
                            >
                              {locations.map((location: any) => (
                                <option value={Number(location.id)}>{location.name}</option>
                              ))}
                            </Select>
                          </div>
                          <div className={styles.name}>
                            <span>Назва:</span>
                            <Input
                              name={`cranes[${index}].name`}
                              className={styles.nameInput}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              onLine
                            />
                          </div>
                          <div className={styles.available}>
                            <span>Доступний:</span>
                            <Checkbox
                              name={`cranes[${index}].isAvailable`}
                              className={styles.availableCheck}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label=""
                            />
                          </div>
                          <div className={styles.rowMin}>
                            <span>Ряд, min:</span>
                            <Input
                              className={styles.rowMinInput}
                              name={`cranes[${index}].accessibilityRowMin`}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              onLine
                            />
                          </div>
                          <div className={styles.rowMax}>
                            <span>Ряд, max:</span>
                            <Input
                              className={styles.rowMaxInput}
                              name={`cranes[${index}].accessibilityRowMax`}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              onLine
                            />
                          </div>
                          <div className={styles.stop}>
                            <span>Зупинка з:</span>
                            <DatePicker
                              name={`cranes[${index}].stopFrom`}
                              format="YYYY-MM-DD HH:mm"
                              showTime={{ defaultValue: moment('00:00:00', 'HH:mm:ss') }}
                              status={
                                // @ts-ignore
                                Object.keys(errors).length !== 0 && errors?.cranes[index]?.stopFrom ? 'error' : ''
                              }
                              minuteStep={15}
                              value={moment(val?.stopFrom)}
                              // @ts-ignore
                              onChange={(dateString: any) =>
                                setFieldValue(`cranes[${index}].stopFrom`, dateString.format('YYYY-MM-DDTHH:mm:00'))
                              }
                              onBlur={handleBlur}
                              allowClear={false}
                            />
                          </div>
                          <div className={styles.start}>
                            <span>Старт з:</span>
                            <DatePicker
                              name={`cranes[${index}].startFrom`}
                              format="YYYY-MM-DD HH:mm"
                              showTime={{ defaultValue: moment('00:00:00', 'HH:mm:ss') }}
                              status={
                                // @ts-ignore
                                Object.keys(errors).length !== 0 && errors?.cranes[index]?.startFrom ? 'error' : ''
                              }
                              minuteStep={15}
                              // @ts-ignore
                              onChange={(dateString: any) =>
                                setFieldValue(`cranes[${index}].startFrom`, dateString.format('YYYY-MM-DDTHH:mm:00'))
                              }
                              value={moment(val?.startFrom)}
                              onBlur={handleBlur}
                              allowClear={false}
                            />
                          </div>
                          <div className={styles.rule}>
                            <span>Увімкнути правило:</span>
                            <Checkbox
                              name={`cranes[${index}].isRuleEnabled`}
                              className={styles.ruleCheck}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label=""
                            />
                          </div>
                          <div className={styles.delete}>
                            <Button
                              variant="danger"
                              className="d-flex justify-content-center align-items-center ml-2 p-1 rounded-circle"
                              onClick={() => deleteHandler(index)}
                            >
                              <Minus size={13} />
                            </Button>
                          </div>
                        </div>
                      ))}
                      <TitleUndescore title="" />
                    </section>
                  );
                }}
              />
              {resource.length !== 0 && <ParalelArivals />}
            </form>
          )}
        </Formik>
      </Container>
    </>
  );
};
export default Resources;
